import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import axios from "axios";
import { MapPin, Locate, Eraser } from "lucide-react";

const DEFAULT_CENTER = [63.870, 16.380]; // Backe
const DEFAULT_ZOOM = 11;

/**
 * Karten-Picker för moderator-redigering.
 * - Klick på kartan placerar/flyttar pinnen → lat/lng uppdateras.
 * - Pinnen är dragbar.
 * - "Hämta adress" → reverse-geocode via Nominatim → fyller adress-fältet.
 * - Tile-switcher Standardkarta / Topografisk.
 *
 * Props:
 *   lat, lng       (number | null)
 *   adress         (string | null)  — visas, kan fyllas av reverse geocode
 *   onChange({lat, lng, adress})
 */
export default function LocationPicker({ lat, lng, adress, onChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const standardRef = useRef(null);
  const topoRef = useRef(null);
  const [tile, setTile] = useState("standard");
  const [geocoding, setGeocoding] = useState(false);
  const [geoError, setGeoError] = useState(null);

  const hasCoords = typeof lat === "number" && typeof lng === "number" && !Number.isNaN(lat) && !Number.isNaN(lng);

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const center = hasCoords ? [lat, lng] : DEFAULT_CENTER;
    const zoom = hasCoords ? 14 : DEFAULT_ZOOM;
    const map = L.map(containerRef.current, { center, zoom, scrollWheelZoom: false });
    mapRef.current = map;

    standardRef.current = L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap</a>',
      maxZoom: 19,
    });
    topoRef.current = L.tileLayer("https://opencache.statkart.no/gatekeeper/gk/gk.open_gmaps?layers=topo4&zoom={z}&x={x}&y={y}", {
      attribution: '© Kartverket',
      maxZoom: 18,
    });
    standardRef.current.addTo(map);

    const pinIcon = L.divIcon({
      html: '<div class="vidalv-picker-pin"><span class="vidalv-picker-pin-core"></span></div>',
      className: "vidalv-leaflet-marker",
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });

    if (hasCoords) {
      markerRef.current = L.marker([lat, lng], { draggable: true, icon: pinIcon }).addTo(map);
      markerRef.current.on("dragend", () => {
        const p = markerRef.current.getLatLng();
        onChange({ lat: Number(p.lat.toFixed(6)), lng: Number(p.lng.toFixed(6)), adress });
      });
    }

    map.on("click", (e) => {
      const { lat: clat, lng: clng } = e.latlng;
      const rounded = { lat: Number(clat.toFixed(6)), lng: Number(clng.toFixed(6)) };
      if (markerRef.current) {
        markerRef.current.setLatLng([clat, clng]);
      } else {
        markerRef.current = L.marker([clat, clng], { draggable: true, icon: pinIcon }).addTo(map);
        markerRef.current.on("dragend", () => {
          const p = markerRef.current.getLatLng();
          onChange({ lat: Number(p.lat.toFixed(6)), lng: Number(p.lng.toFixed(6)), adress });
        });
      }
      onChange({ ...rounded, adress });
    });

    const ro = new ResizeObserver(() => {
      try { map.invalidateSize(); } catch { /* race med unmount */ }
    });
    ro.observe(containerRef.current);
    return () => { ro.disconnect(); map.remove(); mapRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When lat/lng change externally (e.g. parent prop update), reflect on map
  useEffect(() => {
    if (!mapRef.current) return;
    if (hasCoords) {
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        const pinIcon = L.divIcon({
          html: '<div class="vidalv-picker-pin"><span class="vidalv-picker-pin-core"></span></div>',
          className: "vidalv-leaflet-marker",
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        });
        markerRef.current = L.marker([lat, lng], { draggable: true, icon: pinIcon }).addTo(mapRef.current);
        markerRef.current.on("dragend", () => {
          const p = markerRef.current.getLatLng();
          onChange({ lat: Number(p.lat.toFixed(6)), lng: Number(p.lng.toFixed(6)), adress });
        });
      }
    } else if (markerRef.current) {
      mapRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  }, [lat, lng, adress, hasCoords, onChange]);

  const switchTile = (which) => {
    setTile(which);
    if (!mapRef.current) return;
    mapRef.current.removeLayer(which === "standard" ? topoRef.current : standardRef.current);
    (which === "standard" ? standardRef.current : topoRef.current).addTo(mapRef.current);
  };

  const reverseGeocode = async () => {
    if (!hasCoords) return;
    setGeocoding(true);
    setGeoError(null);
    try {
      const res = await axios.get("https://nominatim.openstreetmap.org/reverse", {
        params: { lat, lon: lng, format: "json", "accept-language": "sv" },
        headers: { Accept: "application/json" },
      });
      const a = res.data?.display_name;
      if (a) {
        onChange({ lat, lng, adress: a });
      } else {
        setGeoError("Ingen adress hittades för den punkten.");
      }
    } catch {
      setGeoError("Adress-hämtning misslyckades. Försök igen om en stund.");
    }
    setGeocoding(false);
  };

  const clearLocation = () => {
    onChange({ lat: null, lng: null, adress });
  };

  return (
    <div data-testid="location-picker" className="border border-warm-border rounded-sm bg-white">
      <div className="px-3 py-2 border-b border-warm-border flex flex-wrap items-center justify-between gap-2 bg-bone-alt">
        <div className="flex items-center gap-2 text-xs text-bark">
          <MapPin size={14} className="text-pine" />
          {hasCoords ? (
            <span data-testid="picker-coords">
              <strong>{lat.toFixed(5)}, {lng.toFixed(5)}</strong>
              {" "}<span className="text-bark/60">— klicka eller dra för att flytta</span>
            </span>
          ) : (
            <span className="text-bark/70">Klicka på kartan för att placera platsen</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-warm-border rounded-sm overflow-hidden text-[11px]">
            <button type="button" onClick={() => switchTile("standard")} data-testid="picker-layer-standard"
              className={`px-2 py-1 font-semibold ${tile === "standard" ? "bg-pine text-parchment" : "bg-white text-ink hover:bg-bone-alt"}`}>Standard</button>
            <button type="button" onClick={() => switchTile("topo")} data-testid="picker-layer-topo"
              className={`px-2 py-1 font-semibold border-l border-warm-border ${tile === "topo" ? "bg-pine text-parchment" : "bg-white text-ink hover:bg-bone-alt"}`}>Topo</button>
          </div>
        </div>
      </div>

      <div ref={containerRef} data-testid="picker-map" className="w-full h-[260px]" />

      <div className="px-3 py-2 border-t border-warm-border flex flex-wrap items-center gap-2 bg-bone-alt">
        <button
          type="button"
          onClick={reverseGeocode}
          disabled={!hasCoords || geocoding}
          data-testid="picker-reverse-geocode"
          className="inline-flex items-center gap-1 text-xs font-semibold text-pine hover:text-pine-hover disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Locate size={12} /> {geocoding ? "Hämtar adress..." : "Hämta adress från karta"}
        </button>
        {hasCoords && (
          <button
            type="button"
            onClick={clearLocation}
            data-testid="picker-clear"
            className="inline-flex items-center gap-1 text-xs text-bark hover:text-falu"
          >
            <Eraser size={12} /> Ta bort koordinater
          </button>
        )}
        <div className="ml-auto flex items-center gap-1.5 text-[11px] text-bark">
          <span>Lat:</span>
          <input
            type="number" step="0.000001" value={lat ?? ""}
            data-testid="picker-lat"
            onChange={(e) => onChange({ lat: e.target.value === "" ? null : Number(e.target.value), lng, adress })}
            className="w-24 px-1.5 py-0.5 border border-warm-border rounded-sm bg-white text-[11px]"
          />
          <span>Lng:</span>
          <input
            type="number" step="0.000001" value={lng ?? ""}
            data-testid="picker-lng"
            onChange={(e) => onChange({ lat, lng: e.target.value === "" ? null : Number(e.target.value), adress })}
            className="w-24 px-1.5 py-0.5 border border-warm-border rounded-sm bg-white text-[11px]"
          />
        </div>
      </div>
      {geoError && <p className="px-3 py-2 text-xs text-falu border-t border-warm-border" data-testid="picker-geo-error">{geoError}</p>}
    </div>
  );
}
